import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import {
  FiPlus,
  FiPackage,
  FiUsers,
  FiActivity,
  FiDollarSign,
  FiCheck,
  FiClock,
  FiBriefcase,
  FiTrash2,
} from 'react-icons/fi';
import LoadingButton from '../components/LoadingButton';
import Modal from '../components/Modal';
import MoreMenu from '../components/MoreMenu';
import { FadeIn, StaggerGrid, StaggerItem, TabFade } from '../components/motion';
import { formatPackageFieldValue } from '../config/servicePackageFields';
import { StatusBadge } from '../components/shared/Ui';
import './Services.css';
import { formatLocalDate, formatLocalDateTime } from '../utils/formatTimestamp';
import formatMoney from '../utils/formatMoney';
import { queryKeys, staleTimes } from '../config/queryKeys';
import { appAlert, appConfirm } from '../utils/appDialog';
import {
  calculatePackageEnrollmentEndDate,
  formatPackageDurationLabel,
  toYmd,
} from '../utils/serviceDuration';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const TABS = [
  { id: 'overview', label: 'Overview', icon: FiActivity },
  { id: 'catalog', label: 'Catalog', icon: FiPackage },
  { id: 'enrollments', label: 'Enrollments', icon: FiUsers },
  { id: 'activities', label: 'Activities', icon: FiClock },
];

const ENROLLMENT_STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const money = formatMoney;

function payoutStatusForDisplay(activity) {
  const raw = String(activity?.payout_status || '').trim().toLowerCase();
  if (raw === 'paid') return 'paid';
  if (raw === 'void' || raw === 'voided') return 'void';
  if (raw === 'pending' || raw === 'approved') return 'pending';
  if (String(activity?.status || '').toLowerCase() === 'void') return 'void';
  return raw || null;
}

function EntitlementBar({ total, used, label, unit }) {
  const t = Number(total) || 0;
  const u = Number(used) || 0;
  const pct = t > 0 ? Math.min(100, (u / t) * 100) : 0;
  const remaining = Math.max(0, t - u);
  return (
    <div className="svc-entitlement">
      <div className="svc-entitlement-head">
        <strong>{label}</strong>
        <span>{remaining} / {t} {unit} left</span>
      </div>
      <div className="svc-entitlement-track">
        <div className="svc-entitlement-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const Services = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const canManage = hasPermission('service_manage');
  const canEnroll = hasPermission('service_enroll') || canManage;
  const canFinance = hasPermission('service_finance_view') || hasPermission('service_finance_manage');

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => searchParams.get('tab') || 'overview');
  const focusSalesDocumentId = searchParams.get('document') || null;
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(null);
  const [enrollmentSearch, setEnrollmentSearch] = useState('');
  const [enrollmentStatusFilter, setEnrollmentStatusFilter] = useState('');
  const [activitySearch, setActivitySearch] = useState('');
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', description: '' });
  const [enrollForm, setEnrollForm] = useState({
    candidate_id: '',
    package_id: '',
    primary_consultant_id: '',
    price_paid: '',
    notes: '',
    starts_at: toYmd(new Date()) || '',
  });
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveForm, setMoveForm] = useState({
    package_id: '',
    starts_at: toYmd(new Date()) || '',
    price_paid: '',
    notes: '',
  });
  const [activityForm, setActivityForm] = useState({
    catalog_item_id: '',
    quantity: 1,
    description: '',
    occurred_at: new Date().toISOString().slice(0, 16),
    hire_confirmed: false,
  });

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== tab) setTab(t);
  }, [searchParams, tab]);

  useEffect(() => {
    if (tab === 'sales') {
      const params = new URLSearchParams();
      params.set('tab', 'documents');
      if (focusSalesDocumentId) params.set('document', focusSalesDocumentId);
      navigate(`/erp/selling?${params.toString()}`, { replace: true });
    } else if (tab === 'finance') {
      navigate('/finance/payouts', { replace: true });
    }
  }, [focusSalesDocumentId, navigate, tab]);

  const selectTab = (next) => {
    setTab(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next && next !== 'overview') nextParams.set('tab', next);
    else nextParams.delete('tab');
    if (next !== 'sales') nextParams.delete('document');
    setSearchParams(nextParams, { replace: true });
  };

  const { data: companies = [] } = useQuery(queryKeys.serviceCompanies, () =>
    api.get('/services/companies', { params: canManage ? {} : {} }).then((r) => r.data)
  );

  const { data: packages = [] } = useQuery(
    queryKeys.servicePackages,
    () => api.get('/services/packages').then((r) => r.data),
    { staleTime: staleTimes.reference }
  );

  const { data: enrollments = [], isLoading: loadingEnrollments } = useQuery(
    queryKeys.serviceEnrollments,
    () => api.get('/services/enrollments').then((r) => r.data)
  );

  const { data: enrollmentDetail, isLoading: loadingDetail } = useQuery(
    ['service-enrollment', selectedEnrollmentId],
    () => api.get(`/services/enrollments/${selectedEnrollmentId}`).then((r) => r.data),
    { enabled: !!selectedEnrollmentId }
  );

  const { data: financeSummary } = useQuery(
    'service-finance-summary',
    () => api.get('/services/finance/summary').then((r) => r.data),
    { enabled: canFinance && (tab === 'overview' || tab === 'finance') }
  );

  const { data: activities = [] } = useQuery(
    queryKeys.serviceActivities,
    () => api.get('/services/activities').then((r) => r.data),
    {
      enabled: tab === 'activities',
      // Keep prior results for overview preview without refetching on overview
      keepPreviousData: true,
      staleTime: staleTimes.lists,
    }
  );

  const isConsultantView = user?.role === 'consultant' && !canManage;
  const { data: workSummary } = useQuery(
    'service-work-summary',
    () => api.get('/services/work-summary').then((r) => r.data),
    { enabled: isConsultantView && tab === 'overview' }
  );

  const { data: candidates = [] } = useQuery(
    queryKeys.serviceCandidates(user?.role),
    () =>
      user?.role === 'admin' || hasPermission('view_all_data')
        ? api.get('/candidates').then((r) => r.data)
        : api.get('/candidates/assigned').then((r) => r.data),
    {
      enabled: canEnroll && user?.role !== 'candidate' && showEnrollModal,
      staleTime: staleTimes.lists,
    }
  );

  const { data: consultants = [] } = useQuery(
    queryKeys.serviceConsultants,
    () => api.get('/users').then((r) => r.data.filter((u) => u.role === 'consultant' && u.is_active)),
    {
      enabled:
        canEnroll &&
        (user?.role === 'admin' || canManage) &&
        (showEnrollModal || showMoveModal),
      staleTime: staleTimes.lists,
    }
  );

  const { data: enrollPackageDetail } = useQuery(
    ['service-package', enrollForm.package_id],
    () => api.get(`/services/packages/${enrollForm.package_id}`).then((r) => r.data),
    { enabled: !!enrollForm.package_id && showEnrollModal }
  );

  const { data: movePackageDetail } = useQuery(
    ['service-package', 'move', moveForm.package_id],
    () => api.get(`/services/packages/${moveForm.package_id}`).then((r) => r.data),
    { enabled: !!moveForm.package_id && showMoveModal }
  );

  const createCompanyMutation = useMutation(
    (payload) => api.post('/services/companies', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('service-companies');
        setShowCompanyModal(false);
        setCompanyForm({ name: '', description: '' });
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteCompanyMutation = useMutation(
    (id) => api.delete(`/services/companies/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('service-companies');
        queryClient.invalidateQueries('service-packages');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const enrollMutation = useMutation(
    (payload) => api.post('/services/enrollments', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('service-enrollments');
        queryClient.invalidateQueries('service-finance-summary');
        setShowEnrollModal(false);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const updateEnrollmentMutation = useMutation(
    ({ id, payload }) => api.put(`/services/enrollments/${id}`, payload),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(['service-enrollment', vars.id]);
        queryClient.invalidateQueries('service-enrollments');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const movePackageMutation = useMutation(
    ({ id, payload }) => api.post(`/services/enrollments/${id}/move`, payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('service-enrollments');
        queryClient.invalidateQueries('service-finance-summary');
        setShowMoveModal(false);
        if (res?.data?.enrollment?.id) {
          setSelectedEnrollmentId(res.data.enrollment.id);
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const activityMutation = useMutation(
    ({ id, payload }) => api.post(`/services/enrollments/${id}/activities`, payload),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(['service-enrollment', vars.id]);
        queryClient.invalidateQueries('service-enrollments');
        queryClient.invalidateQueries('service-activities');
        queryClient.invalidateQueries('service-finance-summary');
        queryClient.invalidateQueries('service-finance-ledger');
        queryClient.invalidateQueries('service-finance-pay-run');
        setShowActivityModal(false);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const visibleTabs = TABS;

  const stats = useMemo(() => {
    const active = enrollments.filter((e) => e.status === 'active').length;
    const completed = enrollments.filter((e) => e.status === 'completed').length;
    return { active, completed, total: enrollments.length };
  }, [enrollments]);

  const filteredEnrollments = useMemo(() => {
    const q = enrollmentSearch.trim().toLowerCase();
    return enrollments.filter((e) => {
      if (enrollmentStatusFilter && String(e.status || '').toLowerCase() !== enrollmentStatusFilter) {
        return false;
      }
      if (!q) return true;
      const hay = [
        e.candidate_first_name,
        e.candidate_last_name,
        e.package_name,
        e.company_name,
        e.consultant_first_name,
        e.consultant_last_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [enrollments, enrollmentSearch, enrollmentStatusFilter]);

  const {
    pageItems: pagedEnrollments,
    pagination: enrollmentPagination,
    setPage: setEnrollmentPage,
  } = useClientPager(filteredEnrollments, {
    resetKey: `${enrollmentSearch}|${enrollmentStatusFilter}`,
  });

  const filteredActivities = useMemo(() => {
    const q = activitySearch.trim().toLowerCase();
    if (!q) return activities;
    return activities.filter((a) => {
      const hay = [
        a.catalog_name,
        a.activity_type,
        a.candidate_first_name,
        a.candidate_last_name,
        a.package_name,
        a.consultant_first_name,
        a.consultant_last_name,
        payoutStatusForDisplay(a),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [activities, activitySearch]);

  const {
    pageItems: pagedActivities,
    pagination: activityPagination,
    setPage: setActivityPage,
  } = useClientPager(filteredActivities, {
    resetKey: activitySearch,
  });

  const openPackagePage = (companyId, packageId = null) => {
    if (packageId) {
      navigate(`/services/packages/${packageId}`);
      return;
    }
    const q = companyId ? `?company_id=${companyId}` : '';
    navigate(`/services/packages/new${q}`);
  };

  return (
    <div className="services-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1>
            <FiBriefcase aria-hidden /> Services
          </h1>
          <p className="page-subtitle">
            Pods, packages, enrollments, and fulfillment. Quotes live under Sales; consultant
            payouts under Finance.
          </p>
        </div>
      </FadeIn>

      <div className="svc-tabs" role="tablist" aria-label="Services sections">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              aria-label={t.label}
              className={`svc-tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => selectTab(t.id)}
            >
              <Icon aria-hidden />
              <span className="svc-tab-label">{t.label}</span>
            </button>
          );
        })}
      </div>

      <TabFade tabKey={tab} className="svc-tab-panel">
      {tab === 'overview' && (
        <div className="svc-overview">
          <StaggerGrid className="svc-stat-grid">
            <StaggerItem className="svc-stat-card">
              <FiUsers className="svc-stat-icon" aria-hidden />
              <div>
                <span className="svc-stat-value">{stats.active}</span>
                <span className="svc-stat-label">Active enrollments</span>
              </div>
            </StaggerItem>
            <StaggerItem className="svc-stat-card">
              <FiCheck className="svc-stat-icon" aria-hidden />
              <div>
                <span className="svc-stat-value">{stats.completed}</span>
                <span className="svc-stat-label">Completed</span>
              </div>
            </StaggerItem>
            {canFinance && financeSummary?.totals && (
              <>
                <StaggerItem className="svc-stat-card svc-stat-card--money">
                  <FiDollarSign className="svc-stat-icon" aria-hidden />
                  <div>
                    <span className="svc-stat-value">
                      {(financeSummary.totals_by_currency || []).length > 1
                        ? (financeSummary.totals_by_currency || [])
                            .map((row) =>
                              money(
                                isConsultantView ? row.hourly_payouts : row.revenue,
                                row.currency
                              )
                            )
                            .join(' · ')
                        : money(
                            isConsultantView
                              ? financeSummary.totals.hourly_payouts
                              : financeSummary.totals.revenue,
                            financeSummary.totals_by_currency?.[0]?.currency
                          )}
                    </span>
                    <span className="svc-stat-label">
                      {isConsultantView ? 'Earned (hourly)' : 'Package revenue'}
                    </span>
                  </div>
                </StaggerItem>
                <StaggerItem className="svc-stat-card svc-stat-card--pending">
                  <FiClock className="svc-stat-icon" aria-hidden />
                  <div>
                    <span className="svc-stat-value">
                      {(financeSummary.totals_by_currency || []).length > 1
                        ? (financeSummary.totals_by_currency || [])
                            .map((row) =>
                              money(
                                (Number(row.pending_payouts) || 0) +
                                  (Number(row.pending_bonuses) || 0),
                                row.currency
                              )
                            )
                            .join(' · ')
                        : money(
                            (financeSummary.totals.pending_payouts || 0) +
                              (financeSummary.totals.pending_bonuses || 0),
                            financeSummary.totals_by_currency?.[0]?.currency
                          )}
                    </span>
                    <span className="svc-stat-label">Pending payouts</span>
                  </div>
                </StaggerItem>
              </>
            )}
          </StaggerGrid>

          {isConsultantView && workSummary && (
            <section className="svc-panel svc-consultant-work">
              <div className="svc-panel-head">
                <div>
                  <h2>My work &amp; payouts</h2>
                  <p className="svc-panel-meta">
                    {workSummary.crm_linked_activities || 0} CRM-linked ·{' '}
                    {money(
                      workSummary.pending_payout || 0,
                      workSummary.totals_by_currency?.[0]?.currency ||
                        workSummary.recent_payouts?.[0]?.currency
                    )}{' '}
                    pending
                  </p>
                </div>
              </div>
              {workSummary.activities?.length === 0 ? (
                <div className="svc-empty-inline">
                  <FiActivity aria-hidden />
                  <p>Complete CRM interactions to log billable work.</p>
                </div>
              ) : (
                <ul className="svc-activity-feed">
                  {workSummary.activities.slice(0, 6).map((a) => (
                    <li key={a.id}>
                      <div className="svc-activity-main">
                        <strong>{a.catalog_name || a.activity_type}</strong>
                        <span>
                          {a.candidate_first_name} {a.candidate_last_name}
                          {a.crm_contact_id ? ' · CRM' : ' · Manual'}
                          {Number(a.payout_amount) > 0
                            ? ` · ${money(a.payout_amount, a.payout_currency || a.package_currency)}`
                            : ''}
                          {payoutStatusForDisplay(a) ? (
                            <>
                              {' '}
                              <StatusBadge status={payoutStatusForDisplay(a)} />
                            </>
                          ) : null}
                        </span>
                      </div>
                      <time>{formatLocalDateTime(a.occurred_at)}</time>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <div className="svc-split">
            <section className="svc-panel">
              <div className="svc-panel-head">
                <div>
                  <h2>Recent enrollments</h2>
                  <p className="svc-panel-meta">
                    {stats.total} {stats.total === 1 ? 'enrollment' : 'enrollments'}
                  </p>
                </div>
                {enrollments.length > 0 && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => selectTab('enrollments')}>
                    View all
                  </button>
                )}
              </div>
              {loadingEnrollments ? (
                <div className="loading">Loading…</div>
              ) : enrollments.length === 0 ? (
                <div className="svc-empty-inline">
                  <FiPackage aria-hidden />
                  <p>No enrollments yet.</p>
                  {canEnroll && user?.role !== 'candidate' && (
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowEnrollModal(true)}>
                      <FiPlus /> Enroll candidate
                    </button>
                  )}
                </div>
              ) : (
                <div className="svc-enrollment-list">
                  {enrollments.slice(0, 8).map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      className="svc-enrollment-row"
                      onClick={() => {
                        setSelectedEnrollmentId(e.id);
                        selectTab('enrollments');
                      }}
                    >
                      <div>
                        <strong>
                          {e.candidate_first_name} {e.candidate_last_name}
                        </strong>
                        <span>
                          {e.package_name} · {e.company_name}
                        </span>
                      </div>
                      <StatusBadge status={e.status} />
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="svc-panel">
              <div className="svc-panel-head">
                <div>
                  <h2>Recent activities</h2>
                  <p className="svc-panel-meta">
                    {activities.length} {activities.length === 1 ? 'activity' : 'activities'}
                  </p>
                </div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => selectTab('activities')}>
                  View all
                </button>
              </div>
              {activities.length === 0 ? (
                <div className="svc-empty-inline">
                  <FiClock aria-hidden />
                  <p>Open Activities to load the latest log.</p>
                </div>
              ) : (
                <ul className="svc-activity-feed">
                  {activities.slice(0, 8).map((a) => (
                    <li key={a.id}>
                      <div className="svc-activity-main">
                        <strong>{a.catalog_name || a.activity_type}</strong>
                        <span>
                          {a.candidate_first_name} {a.candidate_last_name} · {a.quantity} {a.unit || ''}
                          {a.crm_contact_id ? ' · CRM' : ''}
                          {Number(a.payout_amount) > 0
                            ? ` · ${money(a.payout_amount, a.payout_currency || a.package_currency)}`
                            : ''}
                        </span>
                      </div>
                      <time>{formatLocalDateTime(a.occurred_at)}</time>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}

      {tab === 'catalog' && (
        <div className="svc-catalog">
          {companies.length > 0 && (
            <div className="svc-panel-head svc-catalog-toolbar">
              <div>
                <h2>Catalog</h2>
                <p className="svc-panel-meta">
                  {companies.length} {companies.length === 1 ? 'pod' : 'pods'} · {packages.length}{' '}
                  {packages.length === 1 ? 'package' : 'packages'}
                </p>
              </div>
              {canManage && (
                <div className="svc-toolbar-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowCompanyModal(true)}
                  >
                    <FiPlus /> Add pod
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => openPackagePage(companies[0]?.id)}
                  >
                    <FiPackage /> Add package
                  </button>
                </div>
              )}
            </div>
          )}
          {companies.length === 0 ? (
            <div className="svc-empty-state">
              <FiBriefcase className="svc-empty-icon" aria-hidden />
              <h2>No pods yet</h2>
              <p>Create a pod to define packages and pricing.</p>
              {canManage && (
                <button type="button" className="btn btn-primary" onClick={() => setShowCompanyModal(true)}>
                  <FiPlus /> Add pod
                </button>
              )}
            </div>
          ) : (
            companies.map((company) => (
              <section key={company.id} className="svc-company-card">
                <div className="svc-company-head">
                  <div>
                    <h2>{company.name}</h2>
                    <p>{company.description || 'No description'}</p>
                  </div>
                  {canManage && (
                    <MoreMenu
                      label={`Actions for ${company.name}`}
                      items={[
                        {
                          id: 'add-package',
                          label: 'Add package',
                          icon: FiPackage,
                          onSelect: () => openPackagePage(company.id),
                        },
                        {
                          id: 'delete-pod',
                          label: 'Delete pod',
                          icon: FiTrash2,
                          danger: true,
                          disabled: deleteCompanyMutation.isLoading,
                          onSelect: async () => {
                            if (
                              !(await appConfirm(
                                `Delete pod “${company.name}”? Its packages will be removed from the catalog. Existing enrollments are kept.`
                              ))
                            ) {
                              return;
                            }
                            deleteCompanyMutation.mutate(company.id);
                          },
                        },
                      ]}
                    />
                  )}
                </div>
                <div className="svc-package-grid">
                  {packages.filter((p) => p.company_id === company.id).length === 0 ? (
                    <div className="svc-empty-inline">
                      <FiPackage aria-hidden />
                      <p>No packages yet. Add one to get started.</p>
                      {canManage && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => openPackagePage(company.id)}
                        >
                          <FiPlus /> Add package
                        </button>
                      )}
                    </div>
                  ) : null}
                  {packages.filter((p) => p.company_id === company.id).map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      className="svc-package-card svc-package-card--clickable"
                      onClick={() => openPackagePage(company.id, pkg.id)}
                    >
                      <div className="svc-package-card-head">
                        <h3>{pkg.name}</h3>
                        {Number(pkg.item_count) > 0 ? (
                          <span className="svc-package-chip">
                            {pkg.item_count} {Number(pkg.item_count) === 1 ? 'item' : 'items'}
                          </span>
                        ) : null}
                      </div>
                      <p className="svc-package-price">{money(pkg.package_price, pkg.currency)}</p>
                      <div className="svc-package-meta-row">
                        {formatPackageDurationLabel(pkg) ? (
                          <span className="svc-package-chip svc-package-chip--muted">
                            {formatPackageDurationLabel(pkg)}
                          </span>
                        ) : null}
                        {Number(pkg.consultant_bill_rate) ? (
                          <span className="svc-package-chip svc-package-chip--muted">
                            Bill {money(pkg.consultant_bill_rate, pkg.currency)}
                          </span>
                        ) : null}
                        {(pkg.field_count || 0) > 0 ? (
                          <span className="svc-package-chip svc-package-chip--muted">
                            {pkg.field_count} fields
                          </span>
                        ) : null}
                      </div>
                      {pkg.description ? (
                        <p className="svc-package-desc">{pkg.description}</p>
                      ) : null}
                    </button>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      )}

      {tab === 'enrollments' && (
        <div className="svc-enrollments-layout">
          <div className="svc-table-panel">
            <div className="svc-panel-head">
              <div>
                <h2>Enrollments</h2>
                <p className="svc-panel-meta">
                  {filteredEnrollments.length}
                  {filteredEnrollments.length !== enrollments.length
                    ? ` of ${enrollments.length}`
                    : ''}{' '}
                  {filteredEnrollments.length === 1 ? 'enrollment' : 'enrollments'}
                </p>
              </div>
              <div className="svc-toolbar-actions">
                <label className="svc-inline-filter">
                  <span className="visually-hidden">Search enrollments</span>
                  <input
                    type="search"
                    value={enrollmentSearch}
                    onChange={(e) => setEnrollmentSearch(e.target.value)}
                    placeholder="Search…"
                    aria-label="Search enrollments"
                  />
                </label>
                <label className="svc-inline-filter">
                  <span className="visually-hidden">Filter by status</span>
                  <select
                    value={enrollmentStatusFilter}
                    onChange={(e) => setEnrollmentStatusFilter(e.target.value)}
                    aria-label="Filter enrollments by status"
                  >
                    {ENROLLMENT_STATUS_FILTERS.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                {canEnroll && user?.role !== 'candidate' && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowEnrollModal(true)}>
                    <FiPlus /> Enroll candidate
                  </button>
                )}
              </div>
            </div>
            {enrollments.length === 0 ? (
              <div className="svc-empty-inline">
                <FiUsers aria-hidden />
                <p>No enrollments yet. Enroll a candidate in a package to get started.</p>
                {canEnroll && user?.role !== 'candidate' && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowEnrollModal(true)}>
                    <FiPlus /> Enroll candidate
                  </button>
                )}
              </div>
            ) : filteredEnrollments.length === 0 ? (
              <div className="svc-empty-inline">
                <FiUsers aria-hidden />
                <p>No enrollments match this filter.</p>
              </div>
            ) : (
              <div className="svc-table-scroll">
                <table className="svc-table">
                  <thead>
                    <tr>
                      <th scope="col">Candidate</th>
                      <th scope="col">Package</th>
                      <th scope="col">Pod</th>
                      <th scope="col">Consultant</th>
                      <th scope="col">Status</th>
                      <th scope="col">Start</th>
                      <th scope="col">End</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedEnrollments.map((e) => (
                      <tr
                        key={e.id}
                        className={selectedEnrollmentId === e.id ? 'selected' : ''}
                        onClick={() => setSelectedEnrollmentId(e.id)}
                      >
                        <td>
                          <Link to={`/candidates/${e.candidate_id}`} onClick={(ev) => ev.stopPropagation()}>
                            {e.candidate_first_name} {e.candidate_last_name}
                          </Link>
                        </td>
                        <td>{e.package_name}</td>
                        <td>{e.company_name}</td>
                        <td>
                          {e.consultant_first_name
                            ? `${e.consultant_first_name} ${e.consultant_last_name}`
                            : '—'}
                        </td>
                        <td>
                          <StatusBadge status={e.status} />
                        </td>
                        <td>{e.starts_at ? formatLocalDate(e.starts_at) : '—'}</td>
                        <td>{e.ends_at ? formatLocalDate(e.ends_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {filteredEnrollments.length > 0 ? (
              <ListPager
                page={enrollmentPagination.page}
                pageCount={enrollmentPagination.pageCount}
                total={enrollmentPagination.total}
                limit={enrollmentPagination.limit}
                onPageChange={setEnrollmentPage}
              />
            ) : null}
          </div>

          <aside className="svc-enrollment-detail">
            {!selectedEnrollmentId ? (
              <div className="svc-empty-inline">
                <FiPackage aria-hidden />
                <p>Select an enrollment to view fulfillment.</p>
              </div>
            ) : loadingDetail ? (
              <div className="loading">Loading…</div>
            ) : enrollmentDetail ? (
              <>
                <div className="svc-detail-head">
                  <h2>{enrollmentDetail.package_name}</h2>
                  <StatusBadge status={enrollmentDetail.status} />
                </div>
                <p className="svc-detail-candidate">
                  <Link to={`/candidates/${enrollmentDetail.candidate_id}`}>
                    {enrollmentDetail.candidate_first_name} {enrollmentDetail.candidate_last_name}
                  </Link>
                </p>
                <p className="svc-detail-meta">
                  Pod {enrollmentDetail.company_name} ·{' '}
                  {money(enrollmentDetail.price_paid, enrollmentDetail.currency)}
                </p>
                <p className="svc-detail-meta">
                  Start {enrollmentDetail.starts_at ? formatLocalDate(enrollmentDetail.starts_at) : '—'}
                  {' · '}
                  End {enrollmentDetail.ends_at ? formatLocalDate(enrollmentDetail.ends_at) : '—'}
                  {formatPackageDurationLabel(enrollmentDetail)
                    ? ` · ${formatPackageDurationLabel(enrollmentDetail)}`
                    : ''}
                </p>

                {canEnroll && user?.role !== 'candidate' ? (
                  <div className="svc-detail-actions">
                    {enrollmentDetail.status === 'active' ? (
                      <>
                        <LoadingButton
                          type="button"
                          className="btn btn-secondary btn-sm"
                          loading={updateEnrollmentMutation.isLoading}
                          onClick={() =>
                            updateEnrollmentMutation.mutate({
                              id: enrollmentDetail.id,
                              payload: { status: 'completed' },
                            })
                          }
                        >
                          Mark completed
                        </LoadingButton>
                        <p className="svc-form-hint">
                          Active packages cannot be moved. Complete or cancel first so history stays on
                          this package, then move the candidate to a new package.
                        </p>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          setMoveForm({
                            package_id: '',
                            starts_at: toYmd(new Date()) || '',
                            price_paid: '',
                            notes: '',
                          });
                          setShowMoveModal(true);
                        }}
                      >
                        Move to another package
                      </button>
                    )}
                  </div>
                ) : null}

                <h3>Entitlements</h3>
                <div className="svc-entitlements">
                  {(enrollmentDetail.entitlements || []).map((ent) => (
                    <EntitlementBar
                      key={ent.id}
                      total={ent.quantity_total}
                      used={ent.quantity_used}
                      label={ent.catalog_name}
                      unit={ent.unit}
                    />
                  ))}
                  {(enrollmentDetail.entitlements || []).length === 0 && (
                    <div className="svc-empty-inline">
                      <p>No entitlements on this enrollment.</p>
                    </div>
                  )}
                </div>

                {canEnroll && enrollmentDetail.status === 'active' && (
                  <button
                    type="button"
                    className="btn btn-primary svc-log-btn"
                    onClick={() => {
                      setActivityForm({
                        catalog_item_id: enrollmentDetail.entitlements?.[0]?.catalog_item_id || '',
                        quantity: 1,
                        description: '',
                        occurred_at: new Date().toISOString().slice(0, 16),
                        hire_confirmed: false,
                      });
                      setShowActivityModal(true);
                    }}
                  >
                    <FiPlus /> Log activity
                  </button>
                )}

                <h3>Activity history</h3>
                {(enrollmentDetail.activities || []).length === 0 ? (
                  <div className="svc-empty-inline">
                    <p>No activity logged yet.</p>
                  </div>
                ) : (
                  <ul className="svc-activity-feed compact">
                    {(enrollmentDetail.activities || []).map((a) => (
                      <li key={a.id}>
                        <strong>{a.catalog_name || a.activity_type}</strong> — {a.quantity}{' '}
                        {a.unit || ''}
                        {a.crm_contact_id ? ' · CRM' : ''}
                        {a.hire_confirmed ? ' · hire confirmed' : ''}
                        {payoutStatusForDisplay(a) ? (
                          <>
                            {' '}
                            <StatusBadge status={payoutStatusForDisplay(a)} />
                          </>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </aside>
        </div>
      )}

      {tab === 'activities' && (
        <div className="svc-table-panel">
          <div className="svc-panel-head">
            <div>
              <h2>Activities</h2>
              <p className="svc-panel-meta">
                {filteredActivities.length}
                {filteredActivities.length !== activities.length ? ` of ${activities.length}` : ''}{' '}
                {filteredActivities.length === 1 ? 'activity' : 'activities'}
              </p>
            </div>
            <div className="svc-toolbar-actions">
              <label className="svc-inline-filter">
                <span className="visually-hidden">Search activities</span>
                <input
                  type="search"
                  value={activitySearch}
                  onChange={(e) => setActivitySearch(e.target.value)}
                  placeholder="Search…"
                  aria-label="Search activities"
                />
              </label>
            </div>
          </div>
          {activities.length === 0 ? (
            <div className="svc-empty-inline">
              <FiClock aria-hidden />
              <p>No activities logged yet.</p>
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="svc-empty-inline">
              <FiClock aria-hidden />
              <p>No activities match this search.</p>
            </div>
          ) : (
            <div className="svc-table-scroll">
              <table className="svc-table">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Type</th>
                    <th scope="col">Candidate</th>
                    <th scope="col">Package</th>
                    <th scope="col">Consultant</th>
                    <th scope="col">Qty</th>
                    <th scope="col">Source</th>
                    <th scope="col">Payout</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedActivities.map((a) => (
                    <tr key={a.id}>
                      <td>{formatLocalDateTime(a.occurred_at)}</td>
                      <td>{a.catalog_name || a.activity_type}</td>
                      <td>
                        {a.candidate_first_name} {a.candidate_last_name}
                      </td>
                      <td>{a.package_name}</td>
                      <td>
                        {a.consultant_first_name
                          ? `${a.consultant_first_name} ${a.consultant_last_name}`
                          : '—'}
                      </td>
                      <td>
                        {a.quantity} {a.unit || ''}
                      </td>
                      <td>
                        {a.source === 'google_calendar' ||
                        (a.description && String(a.description).startsWith('Google Calendar'))
                          ? 'Calendar verified'
                          : a.source === 'crm' || a.crm_contact_id
                            ? 'CRM'
                            : 'Manual'}
                      </td>
                      <td>
                        {Number(a.payout_amount) > 0
                          ? money(a.payout_amount, a.payout_currency || a.package_currency)
                          : '—'}
                      </td>
                      <td>
                        {payoutStatusForDisplay(a) ? (
                          <StatusBadge status={payoutStatusForDisplay(a)} />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filteredActivities.length > 0 ? (
            <ListPager
              page={activityPagination.page}
              pageCount={activityPagination.pageCount}
              total={activityPagination.total}
              limit={activityPagination.limit}
              onPageChange={setActivityPage}
            />
          ) : null}
        </div>
      )}

      </TabFade>

      <Modal open={showCompanyModal} onClose={() => setShowCompanyModal(false)} ariaLabel="Add pod">
        <h2>Add pod</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createCompanyMutation.mutate(companyForm);
          }}
        >
          <div className="form-group">
            <label>Pod name</label>
            <input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={companyForm.description} onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })} rows={3} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowCompanyModal(false)}>Cancel</button>
            <LoadingButton type="submit" className="btn btn-primary" loading={createCompanyMutation.isLoading}>Create</LoadingButton>
          </div>
        </form>
      </Modal>

<Modal open={showEnrollModal} onClose={() => setShowEnrollModal(false)} ariaLabel="Enroll candidate">
        <h2>Enroll candidate in package</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            enrollMutation.mutate({
              candidate_id: Number(enrollForm.candidate_id),
              package_id: Number(enrollForm.package_id),
              primary_consultant_id: enrollForm.primary_consultant_id
                ? Number(enrollForm.primary_consultant_id)
                : null,
              price_paid: enrollForm.price_paid !== '' ? Number(enrollForm.price_paid) : undefined,
              notes: enrollForm.notes,
              starts_at: enrollForm.starts_at || undefined,
            });
          }}
        >
          <div className="form-group">
            <label>Candidate</label>
            <select value={enrollForm.candidate_id} onChange={(e) => setEnrollForm({ ...enrollForm, candidate_id: e.target.value })} required>
              <option value="">— Select —</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.email})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Package</label>
            <select
              value={enrollForm.package_id}
              onChange={(e) => {
                const pkg = packages.find((p) => String(p.id) === e.target.value);
                setEnrollForm({
                  ...enrollForm,
                  package_id: e.target.value,
                  price_paid: pkg ? pkg.package_price : '',
                });
              }}
              required
            >
              <option value="">— Select —</option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>{p.name} — {p.company_name} ({money(p.package_price, p.currency)})</option>
              ))}
            </select>
          </div>
          <div className="svc-form-grid svc-form-grid--2">
            <div className="form-group">
              <label>Service start date</label>
              <input
                type="date"
                value={enrollForm.starts_at}
                onChange={(e) => setEnrollForm({ ...enrollForm, starts_at: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>End date (auto)</label>
              <input
                type="text"
                readOnly
                value={
                  calculatePackageEnrollmentEndDate(
                    enrollForm.starts_at,
                    enrollPackageDetail ||
                      packages.find((p) => String(p.id) === String(enrollForm.package_id))
                  ) || '— (set package valid period)'
                }
              />
              <p className="svc-form-hint">
                Calculated from package valid period (
                {formatPackageDurationLabel(
                  enrollPackageDetail ||
                    packages.find((p) => String(p.id) === String(enrollForm.package_id))
                ) || 'not set'}
                ). Each candidate can have a different start/end.
              </p>
            </div>
          </div>
          <div className="form-group">
            <label>Primary consultant</label>
            <select value={enrollForm.primary_consultant_id} onChange={(e) => setEnrollForm({ ...enrollForm, primary_consultant_id: e.target.value })}>
              <option value="">— Optional —</option>
              {consultants.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Price paid</label>
            <input type="number" min="0" step="0.01" value={enrollForm.price_paid} onChange={(e) => setEnrollForm({ ...enrollForm, price_paid: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={enrollForm.notes} onChange={(e) => setEnrollForm({ ...enrollForm, notes: e.target.value })} rows={2} />
          </div>
          {enrollPackageDetail?.custom_fields?.length > 0 && (
            <div className="svc-enroll-package-fields">
              <h4>Package details</h4>
              <ul>
                {enrollPackageDetail.custom_fields.map((field) => (
                  <li key={field.id || field.label}>
                    <strong>{field.label}:</strong> {formatPackageFieldValue(field)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {enrollPackageDetail?.items?.length > 0 && (
            <div className="svc-enroll-package-fields">
              <h4>Included</h4>
              <ul>
                {enrollPackageDetail.items
                  .filter((item) => Number(item.quantity) > 0)
                  .map((item) => (
                    <li key={item.id || item.catalog_item_id}>
                      {item.custom_label || item.catalog_name}: {item.quantity} {item.unit}
                      {item.notes ? ` — ${item.notes}` : ''}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowEnrollModal(false)}>Cancel</button>
            <LoadingButton type="submit" className="btn btn-primary" loading={enrollMutation.isLoading}>Enroll</LoadingButton>
          </div>
        </form>
      </Modal>

      <Modal open={showMoveModal} onClose={() => setShowMoveModal(false)} ariaLabel="Move service package">
        <h2>Move to another package</h2>
        <p className="svc-form-hint">
          Creates a <strong>new</strong> enrollment. The previous package, activities, and payouts stay
          unchanged for history.
        </p>
        {enrollmentDetail?.status === 'active' ? (
          <p className="svc-form-hint">
            Active enrollment cannot be moved. Mark it completed first.
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!selectedEnrollmentId) return;
              movePackageMutation.mutate({
                id: selectedEnrollmentId,
                payload: {
                  package_id: Number(moveForm.package_id),
                  starts_at: moveForm.starts_at || undefined,
                  price_paid: moveForm.price_paid !== '' ? Number(moveForm.price_paid) : undefined,
                  notes: moveForm.notes || undefined,
                },
              });
            }}
          >
            <div className="form-group">
              <label>New package</label>
              <select
                value={moveForm.package_id}
                onChange={(e) => {
                  const pkg = packages.find((p) => String(p.id) === e.target.value);
                  setMoveForm({
                    ...moveForm,
                    package_id: e.target.value,
                    price_paid: pkg ? String(pkg.package_price ?? '') : '',
                  });
                }}
                required
              >
                <option value="">— Select —</option>
                {packages
                  .filter((p) => String(p.id) !== String(enrollmentDetail?.package_id))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.company_name} ({money(p.package_price, p.currency)})
                    </option>
                  ))}
              </select>
            </div>
            <div className="svc-form-grid svc-form-grid--2">
              <div className="form-group">
                <label>Service start date</label>
                <input
                  type="date"
                  value={moveForm.starts_at}
                  onChange={(e) => setMoveForm({ ...moveForm, starts_at: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>End date (auto)</label>
                <input
                  type="text"
                  readOnly
                  value={
                    calculatePackageEnrollmentEndDate(
                      moveForm.starts_at,
                      movePackageDetail
                    ) || '— (set package valid period)'
                  }
                />
              </div>
            </div>
            <div className="form-group">
              <label>Price paid</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={moveForm.price_paid}
                onChange={(e) => setMoveForm({ ...moveForm, price_paid: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={moveForm.notes}
                onChange={(e) => setMoveForm({ ...moveForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowMoveModal(false)}>
                Cancel
              </button>
              <LoadingButton
                type="submit"
                className="btn btn-primary"
                loading={movePackageMutation.isLoading}
              >
                Move package
              </LoadingButton>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={showActivityModal} onClose={() => setShowActivityModal(false)} ariaLabel="Log service activity">
        <h2>Log fulfillment activity</h2>
        <p className="form-hint" style={{ marginBottom: 12 }}>
          For calls and meetings, prefer Google Calendar sync so time is verified from the booked
          appointment rather than entered manually.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const ent = enrollmentDetail?.entitlements?.find((x) => String(x.catalog_item_id) === String(activityForm.catalog_item_id));
            activityMutation.mutate({
              id: selectedEnrollmentId,
              payload: {
                catalog_item_id: Number(activityForm.catalog_item_id),
                activity_type: ent?.code || 'custom',
                quantity: Number(activityForm.quantity),
                unit: ent?.unit,
                description: activityForm.description,
                occurred_at: new Date(activityForm.occurred_at).toISOString(),
                hire_confirmed: activityForm.hire_confirmed,
                consultant_id: enrollmentDetail?.primary_consultant_id || user?.id,
                source: 'manual',
              },
            });
          }}
        >
          <div className="form-group">
            <label>Entitlement</label>
            <select
              value={activityForm.catalog_item_id}
              onChange={(e) => setActivityForm({ ...activityForm, catalog_item_id: e.target.value })}
              required
            >
              {(enrollmentDetail?.entitlements || []).map((ent) => (
                <option key={ent.catalog_item_id} value={ent.catalog_item_id}>
                  {ent.catalog_name} ({Math.max(0, ent.quantity_total - ent.quantity_used)} left)
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Quantity</label>
            <input type="number" min="0.01" step="0.01" value={activityForm.quantity} onChange={(e) => setActivityForm({ ...activityForm, quantity: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <input value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} placeholder="e.g. Mock interview #2" />
          </div>
          <div className="form-group">
            <label>Occurred at</label>
            <input type="datetime-local" value={activityForm.occurred_at} onChange={(e) => setActivityForm({ ...activityForm, occurred_at: e.target.value })} />
          </div>
          <div className="form-group">
            <label>
              <input type="checkbox" checked={activityForm.hire_confirmed} onChange={(e) => setActivityForm({ ...activityForm, hire_confirmed: e.target.checked })} />
              {' '}Candidate hired — trigger placement bonus
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowActivityModal(false)}>Cancel</button>
            <LoadingButton type="submit" className="btn btn-primary" loading={activityMutation.isLoading}>Log activity</LoadingButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Services;
